import { Injectable, NgZone } from '@angular/core';
import { Observable, catchError, finalize, throwError, filter, map, tap } from 'rxjs';
import { environment } from '.././../../../environments/environment';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHeaders, HttpEventType } from '@angular/common/http';


@Injectable({
  providedIn: 'root'
})
export class HttpService {
  constructor(
    private httpClient: HttpClient,
    private toastr: ToastrService,
    private router: Router,
    private ngZone: NgZone
  ) { }

  /** Helper: extract token from the auth service's currentUser store. */
  private _getAuthHeaders(): { token: string | null } {
    try {
      const stored = localStorage.getItem('currentUser');
      if (!stored) return { token: null };
      const parsed = JSON.parse(stored);
      return { token: parsed?.token || null };
    } catch {
      return { token: null };
    }
  }

  postData(post_url: string, post_data: any): Observable<any> {
    const url = `${environment.apiUrl}/${post_url}`;

    let headers = new HttpHeaders();
    if (!(post_data instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const { token } = this._getAuthHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.httpClient.post<any>(url, post_data, { headers, observe: 'events', reportProgress: true }).pipe(
      filter((event) => event.type === HttpEventType.Response),
      map((event: any) => event.body),
      tap((response) => {
        if (response.toast === 'true' && response.message) {
          this.handleToast(response);
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  putData(put_url: string, put_data: any): Observable<any> {
    const url = `${environment.apiUrl}/${put_url}`;

    let headers = new HttpHeaders();
    if (!(put_data instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const { token } = this._getAuthHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.httpClient.put<any>(url, put_data, { headers: headers, observe: 'events', reportProgress: true }).pipe(
      filter((event) => event.type === HttpEventType.Response),
      map((event: any) => event.body),
      tap((response) => {
        if (response.toast === 'true' && response.message) {
          this.handleToast(response);
        }
      }),
      catchError((error) => throwError(() => error)),
      finalize(() => { })
    );
  }

  patchData(patch_url: string, patch_data: any): Observable<any> {
    const url = `${environment.apiUrl}/${patch_url}`;

    let headers = new HttpHeaders();
    if (!(patch_data instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const { token } = this._getAuthHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.httpClient.patch<any>(url, patch_data, { headers: headers, observe: 'events', reportProgress: true }).pipe(
      filter((event) => event.type === HttpEventType.Response),
      map((event: any) => event.body),
      tap((response) => {
        if (response.toast === 'true' && response.message) {
          this.handleToast(response);
        }
      }),
      catchError((error) => throwError(() => error)),
      finalize(() => { })
    );
  }

  deleteData(delete_url: string): Observable<any> {
    const url = `${environment.apiUrl}/${delete_url}`;

    const { token } = this._getAuthHeaders();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.httpClient.delete<any>(url, {
      headers: headers,
      observe: 'events',
      reportProgress: true
    }).pipe(
      filter(event => event.type === HttpEventType.Response),
      map((event: any) => event.body),
      tap(response => {
        if (response?.toast === 'true' && response?.message) {
          this.handleToast(response);
        }
      }),
      catchError(error => throwError(() => error)),
      finalize(() => { })
    );
  }

  getData(get_url: string) {

    const { token } = this._getAuthHeaders();
    let getHeaders = new HttpHeaders();
    if (token) {
      getHeaders = getHeaders.set('Authorization', `Bearer ${token}`);
    }

    const httpGetOptions = {
      headers: getHeaders,
      withCredentials: true
    };

    const gettingUrl = environment.apiUrl + '/' + get_url.toString();

    return new Observable((observer) => {
      this.httpClient.get<any>(gettingUrl, httpGetOptions).subscribe(
        (e) => {

          const response = e.body || e;
          if (response) {

            if (response.toast == 'true' && response.message) {
              this.handleToast(response);
            }
            this.ngZone.run(() => {
              observer.next(response);
              observer.complete();
            });
          }
        },
        (error) => {
          this.ngZone.run(() => {
            observer.error(error);
            observer.complete();
          });
        }
      );
    });
  }

  downloadExcel(inc_url: any): Observable<Blob> {
    const url = `${environment.apiUrl}/${inc_url}`;
    const { token } = this._getAuthHeaders();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.httpClient.get(url, { headers: headers, responseType: 'blob' });
  }

  private handleToast(response: any) {
    if (!response.message) return;
    switch (response.type) {
      case 'success':
        this.toastr.success(response.message, 'Success');
        break;
      case 'error':
        this.toastr.error(response.message, 'Error');
        break;
      case 'info':
        this.toastr.info(response.message, 'Info');
        break;
      default:
        this.toastr.warning(response.message, 'Warning');
    }
  }

  getBlobData(get_url: string): Observable<Blob> {
    const url = `${environment.apiUrl}/${get_url}`;
    const { token } = this._getAuthHeaders();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.httpClient.get(url, { headers: headers, responseType: 'blob' });
  }
}
